<template>
  <div
    :class="{
      'stickers-upsell': true,
      'stickers-upsell--compact': isCompact,
      'stickers-upsell--multiple': isMultiple
    }"
  >
    <div v-if="isLoading" class="stickers-upsell__loader">
      <Spinner class="stickers-upsell__spinner" />
    </div>
    <template v-else>
      <slot name="header" :artwork="artwork">
        <div class="stickers-upsell__header">
          <div
            class="stickers-upsell__image"
            :class="{ 'stickers-upsell__placeholder': !isImageRendered }"
          >
            <img :src="imageUrl" @load="handleImageLoad" />
          </div>

          <div class="stickers-upsell__details">
            <span class="stickers-upsell__badge">NEXT DAY DELIVERY!</span>
            <UseSvg icon="jiffy-stickers-inline" class="stickers-upsell__logo" />
            <h1 class="stickers-upsell__title">Add Matching Stickers!</h1>
            <div v-if="productRating.avgRating" class="stickers-upsell__reviews_container">
              <Stars
                :stars="productRating.avgRating"
                star-class-name="stars__image--small"
                star-color="#D97706"
              ></Stars>
              {{ productRating.reviewsCount }}
            </div>
            <button
              v-if="isMultiple"
              class="stickers-upsell__action"
              @click.prevent="handleAddMultiple"
            >
              Add All Stickers
            </button>
          </div>
        </div>
      </slot>
      <div v-if="artwork && !isMultiple" class="stickers-upsell__content">
        <UseSvg
          v-if="!isMobile && !isCartPage"
          icon="jiffy-stickers-logo"
          class="stickers-upsell__logo"
        />
        <slot name="info">
          <div class="stickers-upsell__info">
            <h1 class="stickers-upsell__title">Add Matching Stickers!</h1>
            <div v-if="productRating.avgRating" class="stickers-upsell__reviews_container">
              <Stars :stars="productRating.avgRating" size="S"></Stars>
              {{ productRating.reviewsCount }}
            </div>
          </div>
        </slot>
        <div class="stickers-upsell__section">
          <label class="stickers-upsell__label">Select Size</label>
          <StickersUpsellSizes :value="size" :sizes="calculatedSizes" @change="handleSizeChange" />
        </div>
        <div class="stickers-upsell__section">
          <label class="stickers-upsell__label">Select Quantity</label>
          <StickersUpsellQuantity
            :artwork="artwork"
            :size="size"
            :value="quantity"
            @change="handleQuantityChange"
          />
          <div class="stickers-upsell__sub-text">
            <strong>No extra shipping costs</strong> <br v-if="isMobile" />when ordered with Jiffy
            Transfers
          </div>
        </div>
        <div class="stickers-upsell__section">
          <slot
            name="action"
            :isDisabled="isDisabled"
            :isAddedToCart="isAddedToCart"
            :isAddingToCart="isAddingToCart"
            :onAdd="handleAdd"
          >
            <button
              :disabled="isDisabled"
              class="stickers-upsell__action"
              @click.prevent="handleAdd"
            >
              <template v-if="isAddedToCart"> ADDED TO CART! </template>
              <template v-else>
                <Spinner v-if="isAddingToCart" theme="white" class="stickers-upsell__spinner" />
                ADD TO CART
              </template>
            </button>
          </slot>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { api } from "jiffy-api";
import { EventBus, Events } from "jiffy-store";
import { redirectTo, isMobile as isMobileFn } from "common/utils/browser";
import { writeCookie } from "common/utils/cookie";
import { sleep } from "common/utils/helpers/delay";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import Spinner from "components/jiffy/vue-cart/Spinner.vue";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { objNamesToCamelCase, objNamesToSnakeCase } from "common/utils/helpers/cases";
import Stars from "common/vue/components/Stars.vue";
import { DEFAULT_STICKER_VALUES } from "common/utils/stickers/constants";
import StickersUpsellSizes from "./StickersUpsellSizes.vue";
import StickersUpsellQuantity from "./StickersUpsellQuantity.vue";

import {
  STICKERS_URL,
  DIE_CUT_PRODUCT_ID,
  DIE_CUT_VARIANT_ID,
  CART_URL,
  SUCCESS_DELAY,
  CUSTOM_VALUE,
  WIDTHS,
  MIN_QUANTITY
} from "./constants";

const isMobile = isMobileFn();

const getOrderNumber = () =>
  document.querySelector(".js-order-number-container")?.dataset?.orderNumber;
const getPdpImage = () => document.querySelector(".js-carousel-image")?.src;

const fetchArtwork = ({ artworkId, productId }) =>
  api.dtf.getArtwork({ id: artworkId }, { product_id: productId });

const props = defineProps({
  artworkId: [Number, null],
  productId: [String, Number],
  page: String,
  catalogNumber: String,
  variant: {
    type: String,
    default: "default",
    validator: value => ["default", "compact"].includes(value)
  },
  upsellData: {
    type: Object,
    default: () => ({})
  },
  isCartPage: {
    type: Boolean,
    default: false
  }
});

const isLoading = ref(false);
const isImageRendered = ref(false);
const isAddingToCart = ref(false);
const isAddedToCart = ref(false);
const multipleArtworkIds = ref([]);
const artwork = ref(null);
const productId = ref(props.productId);
const quantity = ref(null);
const size = ref(null);
const productRating = ref(objNamesToCamelCase(props.upsellData));

const isCompact = computed(() => props.variant === "compact");
const isMultiple = computed(() => multipleArtworkIds.value.length > 1);
const calculatedSizes = computed(() => {
  let ratio = 1.5;
  let isHeightGreater = false;
  if (artwork.value && artwork.value.metadata) {
    const { metadata } = artwork.value;
    ratio = metadata.aspect_ratio;
    isHeightGreater = metadata.height_inch > metadata.width_inch;
  }

  return [
    ...WIDTHS.map(stickerWidth => {
      let height = stickerWidth;
      let width = stickerWidth;

      if (isHeightGreater) {
        height = parseFloat((width / ratio).toFixed(2));
      } else {
        width = parseFloat((height * ratio).toFixed(2));
      }
      const dimension = { width, height };
      const value = `${width}”x${height}”`;

      return { label: value, value, size: dimension };
    }),
    { label: "Custom", value: CUSTOM_VALUE }
  ];
});
const imageUrl = computed(() => {
  if (multipleArtworkIds.value.length) return getPdpImage();
  if (!artwork.value) return null;
  return artwork.value.cutline_url || artwork.value.url;
});
const isDisabled = computed(
  () =>
    isAddedToCart.value ||
    isAddingToCart.value ||
    !size.value ||
    !quantity.value ||
    quantity.value < MIN_QUANTITY
);

const initializeSelectedSize = () => {
  // eslint-disable-next-line prefer-destructuring
  size.value = calculatedSizes.value[1];
};

const loadArtwork = async payload => {
  try {
    isLoading.value = true;

    const data = await fetchArtwork(payload);
    artwork.value = data;
    initializeSelectedSize();
  } finally {
    isLoading.value = false;
  }
};

const handleImageLoad = () => {
  isImageRendered.value = true;
};

const handleSizeChange = newSize => {
  size.value = newSize;
};

const handleQuantityChange = newQuantity => {
  quantity.value = newQuantity;
};

const handleAdd = async () => {
  try {
    isAddingToCart.value = true;

    const orderNumber = getOrderNumber();
    const payload = {
      event_id: `${orderNumber}:${Date.now()}:${DIE_CUT_PRODUCT_ID}`,
      product_id: DIE_CUT_PRODUCT_ID,
      order_number: orderNumber,
      artworks: [
        {
          ...objNamesToSnakeCase(DEFAULT_STICKER_VALUES),
          artwork_id: artwork.value.id,
          width: size.value.size.width,
          height: size.value.size.height,
          quantity: quantity.value,
          variant_id: DIE_CUT_VARIANT_ID,
          metadata: artwork.value.metadata,
          borneo_metadata: null,
          project_id: null
        }
      ],
      pre_cut: false
    };

    const eventPayload = {
      ...payload,
      page: props.page || "PDP",
      catalog_number: props.catalogNumber,
      business_line: "Stickers"
    };

    submitDtfEvent("sticker_upsell_add_sticker_to_cart", eventPayload);

    const { success } = await api.dtf.addToCart(payload);

    if (success) {
      isAddedToCart.value = true;
      await sleep(SUCCESS_DELAY);
      redirectTo(CART_URL);
    }
  } finally {
    isAddingToCart.value = false;
  }
};

const handleAddMultiple = () => {
  writeCookie("uploaded_artwork_ids", JSON.stringify(multipleArtworkIds.value), 300);
  redirectTo(STICKERS_URL);
};

watch(
  () => size.value,
  newValue => {
    if (newValue.value === CUSTOM_VALUE) {
      writeCookie("uploaded_artwork_ids", JSON.stringify([artwork.value.id]), 300);
      redirectTo(STICKERS_URL);
    }
  }
);

watch(
  () => ({ productId: props.productId, artworkId: props.artworkId }),
  newValue => {
    if (newValue.productId && newValue.artworkId) {
      loadArtwork(newValue);

      productId.value = newValue.productId;
    }
  }
);

watch(artwork, newArtwork => {
  if (newArtwork) {
    initializeSelectedSize();
  }
});

onMounted(() => {
  EventBus.on(Events.MOBILE_GRID_SUBMIT, async ({ target }) => {
    const data = target.dtfData;

    if (data.length > 1) {
      multipleArtworkIds.value = data.map(({ id }) => id);
    } else {
      const dtfData = data[0];
      multipleArtworkIds.value = [];
      await loadArtwork({ artworkId: dtfData.id, productId: dtfData.productId });

      productId.value = DIE_CUT_PRODUCT_ID;
    }
  });
});
</script>

<style lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/jiffy/settings";
@import "frontend/init/jiffy/button/mixin.scss";

.stickers-upsell {
  $this: &;

  border-radius: 4px;
  background: #e6effd;
  padding: 12px;
  display: flex;
  flex-direction: column;
  position: relative;

  @include with-tablet {
    align-items: flex-start;
    flex-direction: row;
    gap: 16px;
    padding: 16px;
  }

  &--compact {
    padding: 0;
    display: block;
    background: none;
  }

  &--multiple {
    @include with-tablet {
      padding: 16px 24px;
    }

    #{$this}__header {
      flex: 1;
      gap: 24px;
    }

    #{$this}__image {
      padding: 0;
      border-radius: 4px;
      border: 1px solid rgba($black, 0.11);
      background: $white;

      img {
        max-width: 100px;

        @include with-tablet {
          max-width: 150px;
        }
      }
    }

    #{$this}__details {
      position: relative;
      flex: 1;

      #{$this}__badge {
        top: 0;
        right: 0;
        left: auto;
        font-size: 10px;
        padding: 4px 6px;
      }

      #{$this}__logo {
        position: static;
        width: 147px;
        height: 22px;
      }

      #{$this}__title {
        font-size: 18px;
      }

      #{$this}__logo,
      #{$this}__title {
        @include with-tablet {
          display: block;
        }
      }

      #{$this}__reviews_container {
        @include with-tablet {
          display: flex;
        }
      }
    }

    #{$this}__action {
      width: auto;

      @include with-tablet {
        min-width: 214px;
        margin-top: 8px;
      }
    }
  }

  &__header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-shrink: 0;
    width: 100%;

    @include with-tablet {
      width: auto;
    }
  }

  &__image {
    line-height: 0;
    padding: 12px;

    img {
      max-width: 78px;

      @include with-tablet {
        filter: drop-shadow(3px 0px 2px rgba($black, 0.7));
        max-width: 183px;
      }
    }

    @include with-tablet {
      padding: 48px 20px 24px;
    }
  }

  &__placeholder {
    img {
      min-width: 78px;

      @include with-tablet {
        min-width: 183px;
      }
    }
  }

  &__content {
    flex: 1;
  }

  &__badge {
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 4px;
    background: $badge-green;
    color: $white;
    font-weight: 700;
    text-transform: uppercase;
    line-height: 1;

    @include with-tablet {
      position: absolute;
      top: 0;
      left: 0;
      padding: 5px 16px;
      font-size: 12px;
    }
  }

  &__title {
    margin: 0;
    color: $blue-primary;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.22;

    @include with-tablet {
      font-size: 20px;
    }
  }

  &__label {
    display: block;
    margin: 0 0 4px;
    color: $text-black;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.42;
  }

  &__section {
    & + & {
      margin-top: 16px;
    }
  }

  &__action {
    @include button-red;
    min-height: 44px;
    display: flex;
    align-items: center;
    gap: 12px;
    justify-content: center;
    width: 100%;
    font-weight: 700;
  }

  &__spinner {
    width: 20px;
  }

  &__info {
    display: none;

    @include with-tablet {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      flex-direction: column;
      align-items: flex-start;
    }
  }

  &__logo {
    width: 123px;
    height: 18px;
    flex-shrink: 0;
    margin: 3px 0;

    @include with-tablet {
      position: absolute;
      height: 40px;
      width: 105px;
      right: 10px;
      margin: 0;
    }
  }

  &__details {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;

    #{$this}__logo,
    #{$this}__title,
    #{$this}__reviews_container {
      @include with-tablet {
        display: none;
      }
    }
  }

  &__loader {
    flex: 1;
    padding: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__sub-text {
    font-size: 13px;
    color: $text-black;
    line-height: 1.5;
    margin-top: 10px;
    text-align: center;
  }

  &__reviews_container {
    display: flex;
    align-items: center;
    gap: 5px;
    color: $blue;
    font-weight: 700;
  }
}
</style>
